import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, MessageSquareText, Loader2, RefreshCw, AlertTriangle, CheckCircle2, ListChecks, Lightbulb, CalendarCheck, User } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { formatDate } from "../lib/constants";
import { CategoryBadge } from "../components/CategoryBadge";
import { ImprovableMoments, ToneAnalysis, SegmentChips } from "../components/ReportExtras";
import { FollowUpList } from "../components/FollowUpCard";

const fmtDur = (s) => (s == null ? "—" : `${Math.floor(s / 60)}m ${s % 60}s`);
const ESITO = { positivo: "#34D399", neutro: "#F59E0B", negativo: "#F87171" };
const VAL = { buona: "#34D399", migliorabile: "#F59E0B", "non gestita": "#F87171" };

export default function SessionReport({ session: initial }) {
  const [s, setS] = useState(initial);
  const [tab, setTab] = useState("analisi");

  useEffect(() => {
    if (s.report_stato !== "pending") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/live/sessions/${s.id}`);
        setS(data);
      } catch (e) { toast.error(errorMessage(e)); }
    }, 3000);
    return () => clearInterval(t);
  }, [s.id, s.report_stato]);

  const rebuild = async () => {
    try {
      await api.post(`/live/sessions/${s.id}/report`);
      setS({ ...s, report_stato: "pending" });
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const seller = s.seller_speaker;
  const label = (sp) => (sp == null ? "?" : seller == null ? `Speaker ${sp}` : sp === seller ? "Io" : "Cliente");
  const r = s.report;

  return (
    <div data-testid="session-report-page" className="space-y-8">
      <Link to="/live" data-testid="report-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white"><ArrowLeft className="h-4 w-4" /> Sessioni live</Link>
      <div className="fade-up">
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Analisi post-chiamata</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">{s.titolo || `Sessione del ${formatDate(s.started_at)}`}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs font-mono text-muted-foreground">
          <span>{formatDate(s.started_at)}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDur(s.durata_sec)}</span>
          <span className="inline-flex items-center gap-1"><MessageSquareText className="h-3 w-3" />{s.n_suggerimenti} suggerimenti</span>
          {r?.esito && <span data-testid="report-esito" className="rounded-full px-2.5 py-0.5 font-semibold" style={{ background: `${ESITO[r.esito]}22`, color: ESITO[r.esito] }}>esito {r.esito}</span>}
        </div>
      </div>

      {!s.salva_trascrizione ? (
        <div data-testid="report-not-saved" className="surface p-8 text-center text-sm text-muted-foreground">Per questa sessione hai scelto di non conservare la trascrizione: nessun dato salvato.</div>
      ) : (
        <>
          <div className="inline-flex rounded-full bg-secondary p-1">
            {[["analisi", "Analisi"], ["trascrizione", "Trascrizione"], ["suggerimenti", "Suggerimenti"]].map(([k, l]) => (
              <button key={k} data-testid={`report-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all ${tab === k ? "bg-card text-white shadow" : "text-muted-foreground"}`}>{l}</button>
            ))}
          </div>

          {tab === "analisi" && s.report_stato === "done" && <FollowUpList origineId={s.id} compact />}

          {tab === "analisi" && (
            s.report_stato === "pending" ? (
              <div data-testid="report-pending" className="surface flex items-center gap-3 p-8 text-sm text-muted-foreground"><Loader2 className="h-5 w-5 spin-slow text-primary" /> Sto analizzando la conversazione…</div>
            ) : s.report_stato === "error" || !r ? (
              <div data-testid="report-error" className="surface flex flex-col items-start gap-4 p-8 text-sm sm:flex-row sm:items-center sm:justify-between">
                <span className="inline-flex items-center gap-2 text-muted-foreground"><AlertTriangle className="h-4 w-4 text-amber-400" />{s.n_segmenti === 0 ? "Nessun parlato registrato in questa sessione." : "Analisi non disponibile."}</span>
                {s.n_segmenti > 0 && <button data-testid="report-rebuild-btn" onClick={rebuild} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:border-primary"><RefreshCw className="h-4 w-4" /> Genera analisi</button>}
              </div>
            ) : (
              <div className="stagger grid gap-6 lg:grid-cols-2">
                <Block icon={CheckCircle2} title="Riepilogo" testId="report-riepilogo" className="lg:col-span-2">
                  <p className="text-sm leading-relaxed">{r.riepilogo}</p>
                  <div data-testid="report-metriche" className="mt-4 flex flex-wrap gap-2 text-xs font-mono">
                    {r.punteggio != null && <span className="rounded-full bg-background px-3 py-1">Punteggio <b>{r.punteggio}/10</b></span>}
                    {r.metriche?.tempi_morti && <span className="rounded-full bg-background px-3 py-1">Tempi morti <b>{r.metriche.tempi_morti.count}</b> · {r.metriche.tempi_morti.total_sec}s</span>}
                    {r.metriche?.talk_ratio_venditore != null && <span className="rounded-full bg-background px-3 py-1">Tuo tempo di parola <b>{r.metriche.talk_ratio_venditore}%</b></span>}
                  </div>
                </Block>
                <ImprovableMoments items={r.momenti_migliorabili || []} />
                <ToneAnalysis voce={r.voce} tono={r.analisi_tono} />
                <Block icon={ListChecks} title={`Obiezioni emerse (${(r.obiezioni || []).length})`} testId="report-obiezioni">
                  {(r.obiezioni || []).length === 0 ? <p className="text-sm text-muted-foreground">Nessuna obiezione rilevata.</p> : (
                    <ul className="space-y-3">
                      {r.obiezioni.map((o, i) => (
                        <li key={i} className="rounded-lg bg-background p-3 text-sm">
                          <div className="flex items-start justify-between gap-3"><p className="font-medium">{o.obiezione}</p>{o.valutazione && <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-mono" style={{ background: `${VAL[o.valutazione] || "#9CA3AF"}22`, color: VAL[o.valutazione] || "#9CA3AF" }}>{o.valutazione}</span>}</div>
                          <p className="mt-1 text-xs text-muted-foreground">{o.gestione}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </Block>
                <Block icon={Lightbulb} title="Momenti chiave" testId="report-momenti"><Bullets items={r.momenti_chiave} /></Block>
                <Block icon={Lightbulb} title="Come migliorare" testId="report-miglioramenti"><Bullets items={r.miglioramenti} /></Block>
                <Block icon={CalendarCheck} title="Follow-up" testId="report-followup"><Bullets items={r.follow_up} /></Block>
                <Block icon={AlertTriangle} title={`Errori dialettici (${(r.errori_dialettici || []).length})`} testId="report-errori-dialettici" className="lg:col-span-2">
                  {(r.errori_dialettici || []).length === 0 ? <p className="text-sm text-muted-foreground">Nessun errore dialettico rilevato.</p> : (
                    <ul className="grid gap-3 md:grid-cols-2">
                      {r.errori_dialettici.map((e, i) => (
                        <li key={i} className="rounded-lg bg-background p-3 text-sm">
                          <p className="font-medium">{e.errore}</p>
                          {e.esempio && <p className="mt-1 text-xs italic text-muted-foreground">“{e.esempio}”</p>}
                          {e.correzione && <p className="mt-1 text-xs text-emerald-300/80">→ {e.correzione}</p>}
                        </li>
                      ))}
                    </ul>
                  )}
                </Block>
              </div>
            )
          )}

          {tab === "trascrizione" && (
            <div data-testid="report-transcript" className="surface space-y-3 p-5">
              {s.transcript.length === 0 ? <p className="text-sm text-muted-foreground">Nessun parlato registrato.</p> : s.transcript.map((seg) => (
                <div key={seg.id} className="flex gap-3">
                  <span className={`mt-0.5 inline-flex h-6 shrink-0 items-center gap-1 rounded-full px-2 text-[11px] font-mono ${seller != null && seg.speaker === seller ? "bg-secondary text-muted-foreground" : "bg-primary/15 text-[#93C5FD]"}`}><User className="h-3 w-3" />{label(seg.speaker)}</span>
                  <p className="min-w-0 text-sm leading-relaxed">{seg.testo}</p>
                  {(seller == null || seg.speaker === seller) && <SegmentChips m={seg.metriche} />}
                </div>
              ))}
            </div>
          )}

          {tab === "suggerimenti" && (
            <div data-testid="report-suggestions" className="stagger space-y-3">
              {s.suggestions.length === 0 ? <div className="surface p-6 text-sm text-muted-foreground">Nessun suggerimento generato.</div> : s.suggestions.map((g) => (
                <div key={g.id} className="surface p-5">
                  <p className="text-xs italic text-muted-foreground">Cliente: “{g.trigger_text}”</p>
                  {g.testo_suggerimento ? <p className="mt-2 text-sm font-medium">{g.testo_suggerimento}</p> : <p className="mt-2 text-sm text-muted-foreground">Nessun suggerimento pertinente nei contenuti.</p>}
                  {g.knowledge_items_usati?.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{g.knowledge_items_usati.map((k) => <CategoryBadge key={k.id} categoria={k.categoria} />)}</div>}
                  <p className="mt-2 text-[11px] font-mono text-muted-foreground">{g.latency_ms} ms</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const Block = ({ icon: Icon, title, testId, className = "", children }) => (
  <section data-testid={testId} className={`surface p-5 ${className}`}>
    <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Icon className="h-4 w-4 text-primary" />{title}</h2>
    {children}
  </section>
);

const Bullets = ({ items = [] }) => items.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : (
  <ul className="space-y-2 text-sm">{items.map((t, i) => <li key={i} className="flex gap-2"><span className="text-primary">•</span><span>{t}</span></li>)}</ul>
);
